import { useEffect } from "react";

import { hasPermission } from "@hz/common/roles";

import { ArrowLeft, ExternalLink, InfoIcon, Mail, PlugIcon, SparkleIcon, UserCog2 } from "lucide-react";
import { useSelector } from "react-redux";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { Box, Button, Flex, Icon, Link, Separator, Text, VStack } from "@chakra-ui/react";

import TrialAwareLayout from "@components/layout";

import { selectInit } from "@state/mondaySlice";

const SETTINGS = [
  {
    name: "Connections",
    href: "/settings/connections",
    icon: <PlugIcon />,
  },
  {
    name: "Permissions",
    href: "/settings/permissions",
    icon: <UserCog2 />,
  },
  {
    name: "Contact Support",
    href: "/settings/contact",
    icon: <Mail />,
  },
  {
    name: "Troubleshoot",
    href: "/settings/troubleshoot",
    icon: <InfoIcon />,
  },
];

export default function SettingsPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const { role } = useSelector(selectInit);
  const canViewConfiguration = hasPermission(role, "configuration", "read");

  useEffect(() => {
    if (!canViewConfiguration) {
      navigate("/");
    }
  }, [canViewConfiguration, navigate]);

  const openChangelogHandler = () => {
    // prettier-ignore
    const url = "https://docs.google.com/document/d/e/2PACX-1vS6CjbHlT_gRLtjX18K6bZ-XM6PG1WvxWzgGHSGoeDSqAAtMxu--skL4nXVz_YM-pM54Vw66lTzaNHB/pub";
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const locationState = location.state;

  return (
    <TrialAwareLayout
      p={0}
      overflow="auto"
    >
      <Flex
        align="flex-start"
        direction="column"
        h="full"
      >
        <Flex
          direction="row"
          w="full"
          h="full"
        >
          {!locationState?.hideSideBar && (
            <VStack
              align="start"
              justifyContent="space-between"
              w="20%"
              minW="200px"
              maxW="250px"
              bg="gray.100"
              p={4}
            >
              <Box>
                <Button
                  variant="subtle"
                  border="1px solid"
                  borderColor="gray.muted"
                  size="sm"
                  h="32px"
                  w="full"
                  onClick={() => navigate("/", { replace: true })}
                >
                  <Icon>
                    <ArrowLeft />
                  </Icon>
                </Button>

                <Separator my="10px" />

                <Text
                  fontWeight="semibold"
                  fontSize="xs"
                  mb="4px"
                >
                  SETTINGS
                </Text>
                {SETTINGS.map((item, index) => {
                  const isActive = location.pathname === item.href.split("?")[0];

                  return (
                    <Link
                      as={NavLink}
                      key={index}
                      // @ts-expect-error: as error
                      to={item.href}
                      w="full"
                      my="4px"
                      textDecorationColor="gray.600"
                    >
                      <Button
                        size="sm"
                        variant={"subtle"}
                        w="full"
                        h="32px"
                        justifyContent="flex-start"
                        bg={isActive ? "gray.200" : undefined}
                        _hover={{ bg: isActive ? "gray.200" : "none" }}
                      >
                        <Icon>{item.icon}</Icon>
                        {item.name}
                      </Button>
                    </Link>
                  );
                })}
              </Box>

              <Box
                w="full"
                textDecorationColor="gray.600"
                mb="30px"
              >
                <Button
                  size="sm"
                  h="32px"
                  variant={"subtle"}
                  w="full"
                  justifyContent="flex-start"
                  onClick={openChangelogHandler}
                  _hover={{
                    textDecorationStyle: "solid",
                    textDecor: "underline",
                    textUnderlineOffset: "3px",
                    textDecorationColor: "gray.600",
                  }}
                >
                  <Icon>
                    <SparkleIcon />
                  </Icon>
                  Changelog
                  <Icon
                    color="gray.500"
                    size="xs"
                    pos="absolute"
                    right="14px"
                  >
                    <ExternalLink />
                  </Icon>
                </Button>
              </Box>
            </VStack>
          )}

          <Box
            flex="1"
            ml="24px"
            my="24px"
          >
            <Outlet />
          </Box>
        </Flex>
      </Flex>
    </TrialAwareLayout>
  );
}
